//Shared function between sector and booking
//dayDifference : return a number of days between end and begin
//XXX : is it the more efficent way to do such a thing?


import { Injectable } from '@angular/core';

import * as moment from 'moment';
import _isString from 'lodash-es/isString';
import _split from 'lodash-es/split';
import { Yii } from 'libraries/interfaces/Yii';
import { LabelService } from 'libraries/service/label/label.service';
import { IMomentDateFormat } from "libraries/interfaces/IMomentDateFormat";

export class CannotCalculatDurationError extends Error {
    constructor(public readonly begin: string, public readonly end: string) {
        super(`Cannot calculate duration when begin datetime (${begin}) is after end datetime (${end})`);
        const labelService = new LabelService();
        this.message = labelService.completeLabel(
            Yii.t("wui-shared-js", `Cannot calculate duration when begin datetime (%begin%) is after end datetime (%end%).`),
            { begin, end }
        );
    }
}
@Injectable()
export class DateService {

    public readonly dateFormats: IMomentDateFormat = JSON.parse(sessionStorage.getItem('dateFormat'));

    public getNow(): Date {
        return new Date();
    }

    public dayDifference(beginParam: string|Date, endParam: string|Date): string {

        const beginStr: string = this.removeOffset(beginParam);
        const endStr: string = this.removeOffset(endParam);

        const begin: moment.Moment = moment.parseZone(beginStr).utc();
        const end: moment.Moment = moment.parseZone(endStr).utc();

        const beginDay: number = begin.dayOfYear();
        const endDay: number = end.dayOfYear();

        let diff: number;

        if (beginDay !== endDay) {
            if (endDay > beginDay) {
                diff = endDay - beginDay;
            } else {
                //from 01/01 to end
                const diff1: number = endDay;

                //get last day of begin year

                const lastDayFirstYear: moment.Moment = moment(begin).endOf('year');
                const lastDayNumber: number = moment(lastDayFirstYear).dayOfYear();

                //from begin to last day of min year
                const diff2: number = lastDayNumber - beginDay;

                //get diff by addition
                //test on
                //https://codepen.io/ejallier/pen/XeRBrq?editors=1111
                //http://projets2.afidium.com/issues/15760
                diff = diff1 + diff2;
            }
        }
        if (diff) {
            return `+ ${diff}`;
        }
        return null;

    }

    public ensureMoment(_date: string | Date | moment.Moment): moment.Moment {
        let output: moment.Moment;
        if (_isString(_date) && _split(_date, '/').length === 3) {
            output = this.strToMoment(_date as string, '/');
        } else if (_isString(_date) && _split(_date, '-').length === 3) {
            output = this.strToMoment(_date as string, '-');
        } else if (moment.isDate(_date)) {
            output = moment(_date);
        } else if (moment.isMoment(_date)) {
            output = _date;
        }
        return output;
    }

    /**
     * Removes timezone offset in resulting string.
     */
    public removeOffset(date: string | Date): string {
        if (typeof date === 'string') {
            const stringDate: string = date as string;
            let outputDate: string = date as string;

            if (stringDate.includes('.')) {
                const splitDate: string[] = stringDate.split('.');
                if (splitDate.length === 2) {
                    outputDate = splitDate[0];
                }
            }

            // #27584: remove +04:00 and -04:00 in dates strings
            if (outputDate.includes('+')) {
                const splitDate: string[] = outputDate.split('+');
                if (splitDate.length === 2) {
                    outputDate = splitDate[0];
                }
            }else if (outputDate.includes('-')) {
                const splitDate: string[] = outputDate.split('-');
                if (splitDate.length === 4) {
                    outputDate = [
                        splitDate[0],
                        splitDate[1],
                        splitDate[2],
                    ].join('-');
                }
            }

            return outputDate as string;
        }

        return moment(date).format();
    }

    /**
     * Transform "YYYY-MM-DD" date string into JS object,
     * and take care of timezone stuff
     */
    public getStr2JsDate(date: string): Date {
        return this.getJs2JsDate(new Date(date));
    }
    public getStr2JsDateAndTime(date: string): Date {
        return this.getJs2JsDateAndTime(new Date(date));
    }

    /**
     * Transform  Js Date into proper JS Date,
     * and take care of timezone stuff
     */
    public getJs2JsDate(date: Date): Date {
        // get proper year, month and day numbers for all timezones
        const y: number = date.getUTCFullYear();
        const mo: number = date.getUTCMonth();
        const d: number = date.getUTCDate();
        // re-construct date with proper values, local mode (because of the use of args, not iso string)
        return new Date(y, mo, d);
    }
    /**
     * Transform  Js Date into proper JS Date,
     * and take care of timezone stuff
     */
    public getJs2JsDateAndTime(date: Date): Date {
        // get proper year, month and day numbers for all timezones
        const y: number = date.getUTCFullYear();
        const mo: number = date.getUTCMonth();
        const d: number = date.getUTCDate();
        const h: number = date.getUTCHours();
        const min: number = date.getUTCMinutes();
        // re-construct date with proper values, local mode (because of the use of args, not iso string)
        return new Date(y, mo, d, h, min);
    }

    public getDuration(minutes: number, timeFormat: string = "HH[h]mm[min]"): string {
        const hours: number = Math.floor((minutes / 60));

        const min: number = minutes - hours * 60;
        const minOutput: string | number = min < 9 ? `0${min}` : min;

        return timeFormat === "HH[h]mm" ? `${hours}h${minOutput}` : `${hours}h${minOutput}min`;
    }

    /**
     * return date with UTC if not null and not inludes UTC to fix #31351
     * @param date
     */
    public addUTCToDate(date: string): string | null {
        if (date) {
            return date.includes('UTC') ? date : `${date} UTC`;
        }
        return null;
    }

    public getDurationFromBeginEnd(begin: string | null, end: string | null, timeFormat: string = "HH[h]mm[min]"): string|null {
        if (!begin || !end) {
            return null;
        }
        const mBegin: moment.Moment = moment(begin);
        const mEnd: moment.Moment = moment(end);

        if (mBegin.isAfter(mEnd)) {
            throw new CannotCalculatDurationError(begin, end);
        }

        const mDiff: number = moment(end).diff(moment(begin));
        const mDuration: moment.Duration = moment.duration(mDiff);

        return timeFormat === "HH[h]mm" ?
            `${Math.floor(mDuration.asHours())}h${moment.utc(mDiff).format('mm')}` :
            `${Math.floor(mDuration.asHours())}h${moment.utc(mDiff).format('mm')}min`;
    }

    public getDayDifferenceFromBeginEnd(begin: string|null, end: string|null): number {
        if (!begin || !end) {
            return null;
        }
        const mBegin: moment.Moment = moment(begin);
        const mEnd: moment.Moment = moment(end);
        return mEnd.diff(mBegin, 'days');
    }

    public getDateFormatted(date: string | Date, dateFormat: string): string {
        const tempDate: string = this.removeOffset(date);
        const dateOutput: moment.Moment = moment.utc(tempDate);
        return dateOutput.format(dateFormat || "LL");
    }

    public getNowAsIsoString(): string {
        return moment().toISOString();
    }

    /**
     * Automatically formats a date for display.
     * * If a date is provided, a formated date is returned
     * * If a date + time is provided, a formated date + time is returned
     * * If a date + time + offset is provided, a formated date + time + offset is returned
     * @param dateStr the input date as an ISO string
     * @return a human readable string
     */
    public formatDateForDisplay(dateStr: string | Date): string {
        if (!dateStr) {
            return null;
        }

        const dateMoment: moment.Moment = moment(dateStr);

        if (typeof dateStr === "string" && this.isDateWithTime(dateStr) && this.isWithOffset(dateStr)){
            // date + time + offset
            return dateMoment.format("LLL Z");
        }

        if (typeof dateStr === "string" && this.isDateWithTime(dateStr)){
            // date + time
            return dateMoment.format("LLL");
        }

        // date only
        return dateMoment.format('LL');
    }

    public formatDateIgnoreOffset(dateTimeStr: string, formatStr: string): string {
        if (!dateTimeStr) {
            return dateTimeStr;
        }

        const momentDate: moment.Moment = this.isWithOffset(dateTimeStr) ?
            moment.parseZone(dateTimeStr) :
            moment(dateTimeStr);

        return momentDate.format(formatStr || "LL");

    }

    private isWithOffset(dateStr: string): boolean {
        return typeof dateStr === "string" && Boolean(dateStr.match(/Z|[+-]\d\d:?\d\d/i));
    }

    private isDateWithTime(dateStr: string): boolean {
        return dateStr.includes('T');
    }

    private ddmmyyyyDate(_date: string): moment.Moment {
        return moment(_date, 'DD/MM/YYYY');
    }

    private yyyymmddDate(_date: string): moment.Moment {
        return moment(_date, 'YYYY/MM/DD');
    }

    private strToMoment(_date: string, separator: string): moment.Moment {
        if (_split(_date, separator)[0].length === 4) {
            return this.yyyymmddDate(_date);
        }
        return this.ddmmyyyyDate(_date);
    }
}
